import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
  buttonSpinner,
  deleteIcon,
  dotsIcon,
  editIcon,
} from "../../../assets/icons";
import { Link } from "react-router-dom";
import { showConfirmDelete } from "../../../lib/confirmDelete";
import { Plus, Search, Copy, Check } from "lucide-react";
import { useUserContext } from "../provider/UserContext";
import { useSelector } from "react-redux";
import { useState } from "react";
import {
  getPlanDetails,
  getOverLimitChecks,
} from "../../../store/userSlice.js";

const UserTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const loggedUser = useSelector((state) => state.user.user);
  const [copiedUserId, setCopiedUserId] = useState(null);
  const [showLimitPanel, setshowLimitPanel] = useState(false);

  const planDetails = useSelector(getPlanDetails);
  const limitChecks = useSelector(getOverLimitChecks);

  const { users, deleteSelectedUser, handleEditUser, loading, handleAddUser } =
    useUserContext();

  const deleteUser = (user) => {
    showConfirmDelete(
      `${user?.firstName || ""} ${user.lastName || ""}`,
      () => {
        deleteSelectedUser(user);
      },
      () => {
        // Do nothing on cancel
      },
    );
  };

  const getPermissionColor = (status) => {
    switch (status) {
      case "Template Manager":
        return "bg-green-100 text-green-800";
      case "Onboarding Manager":
        return "bg-blue-100 text-blue-800";
      case "Super Admin":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Handle search with pagination reset
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      `${user.firstName} ${user.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.designation.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  // Handle pagination navigation
  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Calculate display range for footer
  const startRange =
    filteredUsers.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const endRange = Math.min(currentPage * rowsPerPage, filteredUsers.length);

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border overflow-auto">
      {/* Header */}
      <div className="w-full flex gap-2 max-lg:flex-col items-center max-lg:items-start justify-between p-4 border-b">
        <div className="w-full flex justify-end gap-3">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search User"
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
            />
          </div>

          <div className="relative">
            {limitChecks.usersExceeded ? (
              <button
                onClick={() => setshowLimitPanel(true)}
                className="w-10 h-10 rounded-xl border-2 border-orange-500 bg-orange-50 cursor-pointer text-lg flex items-center justify-center text-orange-500 
                animate-bounce 
                "
              >
                🔒
              </button>
            ) : (
              <button
                onClick={handleAddUser}
                className="flex items-center gap-2 px-4 py-2  bg-lightBlue text-white rounded hover:bg-darkBlue"
              >
                <Plus size={16} />
                Add User
              </button>
            )}

            {limitChecks.usersExceeded && showLimitPanel && (
              <div className="absolute top-[calc(100%+10px)] right-0 z-10 bg-white rounded-2xl p-5 shadow-2xl border border-slate-200 w-[200px] sm:w-[320px] ">
                <div className="absolute -top-2  right-[14px] w-4 h-4 bg-white border border-slate-200 border-b-0 border-r-0 rotate-45" />

                <div className="font-bold text-[15px] mb-1.5 font-sans text-red-600">
                  User limit reached
                </div>

                <p className="text-slate-500 text-[13px] mb-3.5 leading-relaxed">
                  Your{" "}
                  <span className="text-amber-950  font-semibold">
                    {planDetails.plan}
                  </span>{" "}
                  plan allows maximum of{" "}
                  <strong>{planDetails.maxUsers} users</strong>. You've
                  added{" "}
                  <strong>
                    {planDetails.currentUsers}/{planDetails.maxUsers} users
                  </strong>
                  .
                </p>

                <div className="h-1.5 rounded-full bg-slate-100 mb-4">
                  <div className="h-1.5 rounded-full w-full bg-gradient-to-r from-orange-500 to-red-500" />
                </div>

                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center w-full">
                  <Link
                    className="flex items-center justify-center w-full h-[40px] rounded-lg  bg-teal-500 text-white font-semibold text-[13px] no-underline
                  hover:bg-teal-600 transition-colors 
                    hover:scale-[1.02]
                    "
                    to={"/portal/account"}
                  >
                    Upgrade Now
                  </Link>
                  <button
                    className="flex items-center justify-center w-full h-[40px] rounded-lg border-none bg-orange-500  text-white cursor-pointer font-semibold text-[13px]
                      hover:bg-orange-600  transition-colors
                    hover:scale-[1.02]
                    "
                    onClick={() => setshowLimitPanel(false)}
                  >
                    Not Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}

      {users.length === 0 ? (
        <p className="font-bold border-b-2 text-center p-4">
          No users found, Click on the Add User button to add users.
        </p>
      ) : (
        <div className="">
          <div className="overflow-auto max-h-96">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-700">
                    USER<span className="text-sm">(s)</span>
                    <span className="inline-flex items-center ml-2 bg-gray-200 rounded-full px-3 py-0.5 text-sm font-medium text-gray-800">
                      {users.length}
                    </span>
                  </th>
                  <th className="text-left p-4 font-medium text-gray-700 hidden sm:table-cell">
                    DESIGNATION
                  </th>

                  <th className="text-left p-4 font-medium text-gray-700">
                    ASSIGNED PERMISSIONS
                  </th>
                  <th className="text-left p-4 font-medium text-gray-700">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-500">
                      {searchTerm
                        ? "No matches found for your search."
                        : "No users found."}
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <tr
                      key={`${user.id}-${index}`}
                      className="border-t hover:bg-gray-50"
                    >
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>

                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              {user.email}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(user.email);
                                setCopiedUserId(user.id); // store which user was copied
                                setTimeout(() => setCopiedUserId(null), 2000); // reset after 2s
                              }}
                              className={`transition-colors ${
                                copiedUserId === user.id
                                  ? "text-green-600"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                              title="Copy email"
                            >
                              {copiedUserId === user.id ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-900 hidden sm:table-cell">
                        {user.designation}
                      </td>

                      <td className="p-4">
                        {user.role === "super_admin" ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800">
                            SUPER ADMIN
                          </span>
                        ) : user.permissions && user.permissions.length > 0 ? (
                          <div className="flex flex-wrap gap-1 ">
                            {user.permissions.map((permission, idx) => (
                              <span
                                key={idx}
                                className={`w-[135px]   py-1 text-xs text-center font-medium rounded-md ${getPermissionColor(
                                  permission,
                                )}`}
                              >
                                {permission}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 italic">
                            No Permissions assigned
                          </span>
                        )}
                      </td>
                      <td className="p-2 whitespace-nowrap text-center">
                        <Menu>
                          <MenuButton className="">{dotsIcon}</MenuButton>
                          <MenuItems
                            transition
                            anchor="bottom end"
                            className="w-44 origin-center p-1 bg-gray-800 text-white rounded shadow-lg"
                          >
                            <MenuItem>
                              <button
                                className="group flex w-full items-center gap-2 rounded py-1.5 px-3 data-[focus]:bg-white/10 hover:text-green-600"
                                onClick={() => {
                                  if (user.role === "super_admin") {
                                    if (loggedUser.role === "super_admin") {
                                      handleEditUser(user);
                                    } else {
                                      alert(
                                        "You do not have permission to edit this user.",
                                      );
                                    }
                                  } else {
                                    handleEditUser(user);
                                  }
                                }}
                              >
                                <span className="">{editIcon}</span>
                                <span> Edit User</span>
                              </button>
                            </MenuItem>

                            <div className="my-1 h-px bg-gray-500" />

                            <MenuItem>
                              <button
                                className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-white/10 hover:text-red-600"
                                onClick={() => deleteUser(user)}
                              >
                                {loading === "deletingUser" ? (
                                  buttonSpinner
                                ) : (
                                  <>
                                    <span className=""> {deleteIcon}</span>
                                    Delete User
                                  </>
                                )}
                              </button>
                            </MenuItem>
                          </MenuItems>
                        </Menu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 mt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Rows Per Page</span>
              <select
                className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700">
                {filteredUsers.length > 0
                  ? `${String(startRange).padStart(2, "0")}-${String(
                      endRange,
                    ).padStart(2, "0")} of ${String(
                      filteredUsers.length,
                    ).padStart(2, "0")}`
                  : "0-0 of 0"}
              </span>
              <div className="flex gap-1">
                <button
                  className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1 || filteredUsers.length === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 12l-4-4 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 12l4-4-4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;
